import React, { useEffect, useState } from "react";
import axios from "axios";
import { Save, Globe, ChevronDown, ShieldCheck, Download, Trash2, AlertTriangle, History, KeyRound } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PageHeader, Spinner } from "../components/Brand";
import { useApp, API } from "../AppContext";

export default function SettingsPage() {
  const { t, lang, logout } = useApp();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState(false);
  const [domainOpen, setDomainOpen] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [consents, setConsents] = useState(null);
  // 2FA state
  const [twoFa, setTwoFa] = useState({ enabled: false, loading: false });
  const [twoFaOpen, setTwoFaOpen] = useState(false);
  const [twoFaStep, setTwoFaStep] = useState("idle"); // idle|awaitCode
  const [twoFaChallenge, setTwoFaChallenge] = useState(null);
  const [twoFaForm, setTwoFaForm] = useState({ password: "", code: "" });
  const [twoFaMsg, setTwoFaMsg] = useState(""); // success msg
  const [twoFaErr, setTwoFaErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/auth/2fa/status`).then(({ data }) => setTwoFa((t) => ({ ...t, enabled: !!data.two_fa_enabled })));
  }, []);

  const startEnable2FA = async () => {
    setTwoFaErr(""); setTwoFaMsg(""); setTwoFa((t) => ({ ...t, loading: true }));
    try {
      const { data } = await axios.post(`${API}/auth/2fa/request-enable`);
      setTwoFaChallenge(data);
      setTwoFaStep("awaitCode");
    } catch (e) {
      setTwoFaErr(e?.response?.data?.detail || "Erreur");
    } finally { setTwoFa((t) => ({ ...t, loading: false })); }
  };

  const confirmEnable2FA = async () => {
    setTwoFaErr(""); setTwoFa((t) => ({ ...t, loading: true }));
    try {
      await axios.post(`${API}/auth/2fa/toggle`, {
        enable: true,
        password: twoFaForm.password,
        code: twoFaForm.code.trim(),
        challenge_id: twoFaChallenge.challenge_id,
      });
      setTwoFa({ enabled: true, loading: false });
      setTwoFaStep("idle"); setTwoFaChallenge(null);
      setTwoFaForm({ password: "", code: "" });
      setTwoFaMsg(lang === "fr" ? "Double authentification activée." : "Two-factor authentication enabled.");
    } catch (e) {
      setTwoFaErr(e?.response?.data?.detail || "Erreur");
      setTwoFa((t) => ({ ...t, loading: false }));
    }
  };

  const disable2FA = async () => {
    setTwoFaErr(""); setTwoFa((t) => ({ ...t, loading: true }));
    try {
      await axios.post(`${API}/auth/2fa/toggle`, { enable: false, password: twoFaForm.password });
      setTwoFa({ enabled: false, loading: false });
      setTwoFaForm({ password: "", code: "" });
      setTwoFaMsg(lang === "fr" ? "Double authentification désactivée." : "Two-factor authentication disabled.");
    } catch (e) {
      setTwoFaErr(e?.response?.data?.detail || "Erreur");
      setTwoFa((t) => ({ ...t, loading: false }));
    }
  };

  useEffect(() => {
    if (legalOpen && !consents) {
      axios.get(`${API}/auth/me/consents`).then(({ data }) => setConsents(data));
    }
  }, [legalOpen]);

  const exportData = async () => {
    setExporting(true);
    try {
      const { data } = await axios.get(`${API}/auth/me/data-export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `progere-mes-donnees-${new Date().toISOString().slice(0, 10)}.json`; a.click();
      URL.revokeObjectURL(url);
    } finally { setExporting(false); }
  };

  const deleteAccount = async () => {
    const c = window.prompt(lang === "fr"
      ? "Cette action est irréversible. Toutes vos données seront supprimées.\n\nTapez DELETE pour confirmer :"
      : "This is irreversible. All your data will be deleted.\n\nType DELETE to confirm:");
    if (c !== "DELETE") return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/auth/me`, { data: { confirm: "DELETE" } });
      alert(lang === "fr" ? "Votre compte a été supprimé." : "Your account has been deleted.");
      logout();
      nav("/");
    } catch (e) {
      alert(e?.response?.data?.detail || "Erreur");
    } finally { setDeleting(false); }
  };

  useEffect(() => { axios.get(`${API}/settings`).then(({ data }) => setS(data)); }, []);

  const save = async () => {
    setSaving(true);
    try { const { data } = await axios.put(`${API}/settings`, s); setS(data); setOk(true); setTimeout(() => setOk(false), 2000); }
    finally { setSaving(false); }
  };

  if (!s) return <Spinner />;

  return (
    <div>
      <PageHeader title={t.settings_p.title} />
      <div className="glass-card p-6 max-w-3xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{t.settings_p.company}</label>
            <input className="input-dark mt-2" value={s.company || ""} onChange={(e) => setS({ ...s, company: e.target.value })} data-testid="settings-company" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{t.settings_p.gst}</label>
            <input className="input-dark mt-2" value={s.gst_number || ""} onChange={(e) => setS({ ...s, gst_number: e.target.value })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{t.settings_p.qst}</label>
            <input className="input-dark mt-2" value={s.qst_number || ""} onChange={(e) => setS({ ...s, qst_number: e.target.value })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{t.settings_p.profit}</label>
            <input type="number" className="input-dark mt-2" value={s.default_profit || 15} onChange={(e) => setS({ ...s, default_profit: parseFloat(e.target.value) })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{t.settings_p.rate}</label>
            <input type="number" className="input-dark mt-2" value={s.default_hourly_rate || 65} onChange={(e) => setS({ ...s, default_hourly_rate: parseFloat(e.target.value) })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{t.settings_p.markup}</label>
            <input type="number" className="input-dark mt-2" value={s.default_material_markup || 20} onChange={(e) => setS({ ...s, default_material_markup: parseFloat(e.target.value) })} />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{t.settings_p.terms}</label>
            <textarea className="input-dark mt-2" rows={5} value={s.default_terms || ""} onChange={(e) => setS({ ...s, default_terms: e.target.value })} />
          </div>

          {/* CCQ Auto-adjustment toggle */}
          <div className="md:col-span-2 p-5 rounded-2xl" style={{ background: "rgba(212,175,55,0.06)", border: "1px solid rgba(212,175,55,0.25)" }}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: "#D4AF37" }}>{t.settings_p.ccq_title}</span>
                </div>
                <p className="text-xs leading-relaxed mb-3" style={{ color: "rgba(245,245,245,0.65)" }}>{t.settings_p.ccq_desc}</p>
                {s.ccq_auto_adjust ? (
                  s.ccq_hike_active ? (
                    <span className="text-xs font-semibold" style={{ color: "#4ADE80" }}>✓ {t.settings_p.ccq_active}</span>
                  ) : (
                    <span className="text-xs font-semibold" style={{ color: "#D4AF37" }}>⏳ {t.settings_p.ccq_pending}</span>
                  )
                ) : (
                  <span className="text-xs font-semibold" style={{ color: "rgba(245,245,245,0.5)" }}>○ {t.settings_p.ccq_off}</span>
                )}
              </div>
              <label className="relative inline-flex items-center cursor-pointer flex-shrink-0" data-testid="ccq-toggle">
                <input type="checkbox" className="sr-only peer"
                  checked={!!s.ccq_auto_adjust}
                  onChange={(e) => setS({ ...s, ccq_auto_adjust: e.target.checked })} />
                <div className="w-12 h-7 rounded-full peer transition-all"
                  style={{
                    background: s.ccq_auto_adjust ? "#C00000" : "rgba(255,255,255,0.1)",
                    border: "1px solid rgba(245,245,245,0.1)",
                  }}>
                  <div className="w-5 h-5 rounded-full bg-white transition-transform mt-0.5"
                    style={{ transform: s.ccq_auto_adjust ? "translateX(26px)" : "translateX(2px)" }} />
                </div>
              </label>
            </div>
          </div>

          {/* Material Inflation toggle */}
          <div className="md:col-span-2 p-5 rounded-2xl" style={{ background: "rgba(212,175,55,0.06)", border: "1px solid rgba(212,175,55,0.25)" }}>
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: "#D4AF37" }}>{t.settings_p.inflation_title}</span>
                </div>
                <p className="text-xs leading-relaxed mb-3" style={{ color: "rgba(245,245,245,0.65)" }}>{t.settings_p.inflation_desc}</p>
                {s.material_inflation_enabled ? (
                  <span className="text-xs font-semibold" style={{ color: "#4ADE80" }}>
                    ✓ {t.settings_p.inflation_active} (+{(s.material_inflation_pct ?? 2.5).toFixed(1)}%)
                  </span>
                ) : (
                  <span className="text-xs font-semibold" style={{ color: "rgba(245,245,245,0.5)" }}>○ {t.settings_p.inflation_off}</span>
                )}
              </div>
              <label className="relative inline-flex items-center cursor-pointer flex-shrink-0" data-testid="inflation-toggle">
                <input type="checkbox" className="sr-only peer"
                  checked={!!s.material_inflation_enabled}
                  onChange={(e) => setS({ ...s, material_inflation_enabled: e.target.checked })} />
                <div className="w-12 h-7 rounded-full peer transition-all"
                  style={{
                    background: s.material_inflation_enabled ? "#C00000" : "rgba(255,255,255,0.1)",
                    border: "1px solid rgba(245,245,245,0.1)",
                  }}>
                  <div className="w-5 h-5 rounded-full bg-white transition-transform mt-0.5"
                    style={{ transform: s.material_inflation_enabled ? "translateX(26px)" : "translateX(2px)" }} />
                </div>
              </label>
            </div>
            {s.material_inflation_enabled && (
              <div>
                <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{t.settings_p.inflation_pct}</label>
                <input type="number" step="0.1" min="0" max="50" className="input-dark mt-2 max-w-xs"
                  value={s.material_inflation_pct ?? 2.5}
                  onChange={(e) => setS({ ...s, material_inflation_pct: parseFloat(e.target.value) || 0 })}
                  data-testid="inflation-pct" />
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          {ok && <span className="text-sm self-center" style={{ color: "#4ADE80" }}>✓ {t.settings_p.save}</span>}
          <button className="btn-red" onClick={save} disabled={saving} data-testid="settings-save">
            {saving ? <Spinner /> : <Save size={14} />} {t.settings_p.save}
          </button>
        </div>
      </div>

      {/* Custom Domain FAQ */}
      <div className="glass-card p-6 max-w-3xl mt-6" data-testid="custom-domain-section">
        <button onClick={() => setDomainOpen(!domainOpen)} className="w-full flex items-center justify-between gap-4" data-testid="custom-domain-toggle">
          <div className="flex items-center gap-3 text-left">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "rgba(212,175,55,0.12)" }}>
              <Globe size={18} color="#D4AF37" />
            </div>
            <div>
              <div className="font-heading text-base font-bold" style={{ color: "#F5F5F5" }}>
                {lang === "fr" ? "Domaine personnalisé" : "Custom domain"}
              </div>
              <div className="text-xs" style={{ color: "rgba(245,245,245,0.55)" }}>
                {lang === "fr" ? "Connectez votre propre domaine (ex. devis.votreentreprise.ca)" : "Connect your own domain (e.g. quotes.yourcompany.ca)"}
              </div>
            </div>
          </div>
          <ChevronDown size={18} color="rgba(245,245,245,0.6)" style={{ transform: domainOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
        </button>

        {domainOpen && (
          <div className="mt-6 space-y-5 text-sm" style={{ color: "rgba(245,245,245,0.8)" }}>
            <p style={{ color: "rgba(245,245,245,0.7)" }}>
              {lang === "fr"
                ? "Vous pouvez relier votre nom de domaine personnalisé à votre application PROGÈRE après le déploiement. Suivez ces étapes :"
                : "You can connect your custom domain name to your PROGÈRE app after deployment. Follow these steps:"}
            </p>
            <ol className="space-y-4 pl-5 list-decimal">
              <li>
                <strong style={{ color: "#D4AF37" }}>
                  {lang === "fr" ? "Déployez votre application" : "Deploy your application"}
                </strong>
                <p className="mt-1" style={{ color: "rgba(245,245,245,0.65)" }}>
                  {lang === "fr"
                    ? 'Dans Emergent, cliquez sur "Deploy" en haut à droite. Vous obtiendrez une URL .emergent.host.'
                    : 'In Emergent, click "Deploy" at the top right. You will get a .emergent.host URL.'}
                </p>
              </li>
              <li>
                <strong style={{ color: "#D4AF37" }}>
                  {lang === "fr" ? "Achetez un nom de domaine" : "Buy a domain name"}
                </strong>
                <p className="mt-1" style={{ color: "rgba(245,245,245,0.65)" }}>
                  {lang === "fr"
                    ? "Auprès d'un registraire comme Namecheap, OVH, GoDaddy ou Google Domains."
                    : "From a registrar like Namecheap, OVH, GoDaddy or Google Domains."}
                </p>
              </li>
              <li>
                <strong style={{ color: "#D4AF37" }}>
                  {lang === "fr" ? "Configurer le DNS (CNAME)" : "Configure DNS (CNAME)"}
                </strong>
                <p className="mt-1" style={{ color: "rgba(245,245,245,0.65)" }}>
                  {lang === "fr"
                    ? "Dans votre tableau DNS, créez un enregistrement CNAME :"
                    : "In your DNS dashboard, create a CNAME record:"}
                </p>
                <pre className="mt-2 p-3 rounded-lg text-xs font-mono overflow-x-auto" style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(212,175,55,0.15)", color: "#F5F5F5" }}>
{`Type:  CNAME
Name:  www  (ou votre sous-domaine)
Value: <votre-url>.emergent.host
TTL:   3600`}
                </pre>
              </li>
              <li>
                <strong style={{ color: "#D4AF37" }}>
                  {lang === "fr" ? "Ajouter le domaine dans Emergent" : "Add the domain in Emergent"}
                </strong>
                <p className="mt-1" style={{ color: "rgba(245,245,245,0.65)" }}>
                  {lang === "fr"
                    ? 'Dans le tableau de bord Emergent → "Custom Domain", entrez votre nom de domaine. Un certificat SSL sera émis automatiquement.'
                    : 'In Emergent dashboard → "Custom Domain", enter your domain. An SSL certificate will be issued automatically.'}
                </p>
              </li>
              <li>
                <strong style={{ color: "#D4AF37" }}>
                  {lang === "fr" ? "Attendre la propagation" : "Wait for propagation"}
                </strong>
                <p className="mt-1" style={{ color: "rgba(245,245,245,0.65)" }}>
                  {lang === "fr"
                    ? "La propagation DNS peut prendre de 5 minutes à 24 heures."
                    : "DNS propagation can take from 5 minutes to 24 hours."}
                </p>
              </li>
            </ol>
            <div className="mt-4 p-4 rounded-xl" style={{ background: "rgba(212,175,55,0.06)", border: "1px solid rgba(212,175,55,0.2)" }}>
              <div className="text-xs uppercase tracking-widest mb-1" style={{ color: "#D4AF37" }}>
                {lang === "fr" ? "Astuce" : "Tip"}
              </div>
              <p className="text-xs" style={{ color: "rgba(245,245,245,0.75)" }}>
                {lang === "fr"
                  ? "Pour un domaine racine (sans www), utilisez un enregistrement ALIAS / ANAME si votre registraire le supporte, sinon un A record vers l'IP fournie par Emergent."
                  : "For a root domain (no www), use an ALIAS / ANAME record if your registrar supports it, otherwise an A record to the IP provided by Emergent."}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* 2FA / Double authentification */}
      <div className="glass-card p-6 max-w-3xl mt-6" data-testid="twofa-section">
        <button onClick={() => setTwoFaOpen(!twoFaOpen)} className="w-full flex items-center justify-between gap-4" data-testid="twofa-toggle">
          <div className="flex items-center gap-3 text-left">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: twoFa.enabled ? "rgba(74,222,128,0.12)" : "rgba(212,175,55,0.12)" }}>
              <KeyRound size={18} color={twoFa.enabled ? "#4ADE80" : "#D4AF37"} />
            </div>
            <div>
              <div className="font-heading text-base font-bold" style={{ color: "#F5F5F5" }}>
                {lang === "fr" ? "Double authentification (2FA)" : "Two-factor authentication (2FA)"}
              </div>
              <div className="text-xs" style={{ color: twoFa.enabled ? "#4ADE80" : "rgba(245,245,245,0.55)" }}>
                {twoFa.enabled
                  ? (lang === "fr" ? "✓ Activée · code par courriel à chaque connexion" : "✓ Enabled · email code on every sign-in")
                  : (lang === "fr" ? "Recommandée · sécurise votre compte avec un code par courriel" : "Recommended · secure your account with an email code")}
              </div>
            </div>
          </div>
          <ChevronDown size={18} color="rgba(245,245,245,0.6)" style={{ transform: twoFaOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
        </button>

        {twoFaOpen && (
          <div className="mt-6 space-y-4">
            {twoFaMsg && (
              <div className="px-3 py-2 rounded-xl text-sm" style={{ background: "rgba(74,222,128,0.12)", color: "#4ADE80", border: "1px solid rgba(74,222,128,0.3)" }}>{twoFaMsg}</div>
            )}
            {twoFaErr && (
              <div className="px-3 py-2 rounded-xl text-sm" style={{ background: "rgba(192,0,0,0.15)", color: "#FF6B6B", border: "1px solid rgba(192,0,0,0.35)" }}>{twoFaErr}</div>
            )}

            {!twoFa.enabled && twoFaStep === "idle" && (
              <button onClick={startEnable2FA} disabled={twoFa.loading} className="btn-gold" data-testid="twofa-enable-start">
                {twoFa.loading ? <Spinner /> : <><KeyRound size={14} /> {lang === "fr" ? "Activer la 2FA" : "Enable 2FA"}</>}
              </button>
            )}

            {!twoFa.enabled && twoFaStep === "awaitCode" && (
              <div className="space-y-3">
                <p className="text-sm" style={{ color: "rgba(245,245,245,0.75)" }}>
                  {lang === "fr" ? "Un code de vérification a été envoyé à votre adresse courriel." : "A verification code was sent to your email."}
                </p>
                <div>
                  <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{lang === "fr" ? "MOT DE PASSE ACTUEL" : "CURRENT PASSWORD"}</label>
                  <input type="password" className="input-dark mt-2"
                    value={twoFaForm.password} onChange={(e) => setTwoFaForm({ ...twoFaForm, password: e.target.value })}
                    data-testid="twofa-password" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{lang === "fr" ? "CODE À 6 CHIFFRES" : "6-DIGIT CODE"}</label>
                  <input type="text" inputMode="numeric" maxLength={6}
                    className="input-dark mt-2 tracking-[0.4em] font-mono text-center"
                    placeholder="••••••"
                    value={twoFaForm.code}
                    onChange={(e) => setTwoFaForm({ ...twoFaForm, code: e.target.value.replace(/\D/g, "") })}
                    data-testid="twofa-code" />
                </div>
                <div className="flex gap-2">
                  <button onClick={confirmEnable2FA} disabled={twoFa.loading || twoFaForm.code.length !== 6 || !twoFaForm.password} className="btn-gold disabled:opacity-50" data-testid="twofa-confirm-enable">
                    {twoFa.loading ? <Spinner /> : (lang === "fr" ? "Confirmer l'activation" : "Confirm activation")}
                  </button>
                  <button onClick={() => { setTwoFaStep("idle"); setTwoFaChallenge(null); setTwoFaForm({ password: "", code: "" }); }} className="btn-ghost" data-testid="twofa-cancel">
                    {lang === "fr" ? "Annuler" : "Cancel"}
                  </button>
                </div>
              </div>
            )}

            {twoFa.enabled && (
              <div className="space-y-3">
                <p className="text-sm" style={{ color: "rgba(245,245,245,0.75)" }}>
                  {lang === "fr" ? "Pour désactiver la 2FA, confirmez votre mot de passe." : "To disable 2FA, confirm your password."}
                </p>
                <input type="password" className="input-dark"
                  placeholder={lang === "fr" ? "Mot de passe actuel" : "Current password"}
                  value={twoFaForm.password}
                  onChange={(e) => setTwoFaForm({ ...twoFaForm, password: e.target.value })}
                  data-testid="twofa-disable-password" />
                <button onClick={disable2FA} disabled={twoFa.loading || !twoFaForm.password} className="btn-ghost disabled:opacity-50" style={{ borderColor: "rgba(192,0,0,0.4)", color: "#FF6B6B" }} data-testid="twofa-disable">
                  {twoFa.loading ? <Spinner /> : (lang === "fr" ? "Désactiver la 2FA" : "Disable 2FA")}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Loi 25 — Mes droits */}
      <div className="glass-card p-6 max-w-3xl mt-6" data-testid="loi25-section">
        <button onClick={() => setLegalOpen(!legalOpen)} className="w-full flex items-center justify-between gap-4" data-testid="loi25-toggle">
          <div className="flex items-center gap-3 text-left">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "rgba(74,222,128,0.12)" }}>
              <ShieldCheck size={18} color="#4ADE80" />
            </div>
            <div>
              <div className="font-heading text-base font-bold" style={{ color: "#F5F5F5" }}>
                {lang === "fr" ? "Mes droits (Loi 25 du Québec)" : "My rights (Quebec Bill 25)"}
              </div>
              <div className="text-xs" style={{ color: "rgba(245,245,245,0.55)" }}>
                {lang === "fr" ? "Accéder, exporter ou supprimer vos données personnelles" : "Access, export or delete your personal data"}
              </div>
            </div>
          </div>
          <ChevronDown size={18} color="rgba(245,245,245,0.6)" style={{ transform: legalOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
        </button>

        {legalOpen && (
          <div className="mt-6 space-y-5">
            <p className="text-sm" style={{ color: "rgba(245,245,245,0.7)" }}>
              {lang === "fr"
                ? "Conformément à la Loi 25 (Loi modernisant des dispositions législatives en matière de protection des renseignements personnels), vous disposez d'un droit d'accès, de rectification, de portabilité et de suppression de vos données."
                : "Under Quebec's Bill 25, you have the right to access, correct, port and delete your personal data."}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Export */}
              <div className="p-5 rounded-2xl" style={{ background: "rgba(74,222,128,0.06)", border: "1px solid rgba(74,222,128,0.2)" }}>
                <Download size={20} color="#4ADE80" className="mb-3" />
                <div className="font-heading text-sm font-bold mb-1" style={{ color: "#F5F5F5" }}>
                  {lang === "fr" ? "Exporter mes données" : "Export my data"}
                </div>
                <p className="text-xs mb-4" style={{ color: "rgba(245,245,245,0.6)" }}>
                  {lang === "fr" ? "Téléchargez toutes vos données dans un fichier JSON structuré (droit à la portabilité — art. 27)." : "Download all your data as a structured JSON file (right to data portability)."}
                </p>
                <button onClick={exportData} disabled={exporting} className="btn-ghost text-xs w-full justify-center" data-testid="export-data-btn">
                  {exporting ? <Spinner /> : <><Download size={12} /> {lang === "fr" ? "Télécharger le JSON" : "Download JSON"}</>}
                </button>
              </div>

              {/* Delete */}
              <div className="p-5 rounded-2xl" style={{ background: "rgba(192,0,0,0.06)", border: "1px solid rgba(192,0,0,0.25)" }}>
                <AlertTriangle size={20} color="#C00000" className="mb-3" />
                <div className="font-heading text-sm font-bold mb-1" style={{ color: "#F5F5F5" }}>
                  {lang === "fr" ? "Supprimer mon compte" : "Delete my account"}
                </div>
                <p className="text-xs mb-4" style={{ color: "rgba(245,245,245,0.6)" }}>
                  {lang === "fr" ? "Supprime définitivement votre compte et l'ensemble de vos données. Cette action est irréversible." : "Permanently deletes your account and all data. This action is irreversible."}
                </p>
                <button onClick={deleteAccount} disabled={deleting} className="btn-ghost text-xs w-full justify-center" style={{ borderColor: "rgba(192,0,0,0.4)", color: "#FF6B6B" }} data-testid="delete-account-btn">
                  {deleting ? <Spinner /> : <><Trash2 size={12} /> {lang === "fr" ? "Supprimer mon compte" : "Delete my account"}</>}
                </button>
              </div>
            </div>

            <p className="text-xs mt-4" style={{ color: "rgba(245,245,245,0.5)" }}>
              {lang === "fr" ? "Pour toute question, contactez notre Responsable de la protection des renseignements personnels via la " : "For any question, contact our Privacy Officer via the "}
              <a href="/confidentialite" target="_blank" rel="noreferrer" style={{ color: "#D4AF37" }}>
                {lang === "fr" ? "Politique de confidentialité" : "Privacy Policy"}
              </a>.
            </p>

            {/* Consent history */}
            <div className="mt-6 p-5 rounded-2xl" style={{ background: "rgba(245,245,245,0.03)", border: "1px solid rgba(245,245,245,0.08)" }} data-testid="consent-history">
              <div className="flex items-center gap-2 mb-3">
                <History size={14} color="#D4AF37" />
                <div className="text-xs uppercase tracking-wider" style={{ color: "#D4AF37" }}>
                  {lang === "fr" ? "Mon historique de consentements" : "My consent history"}
                </div>
              </div>
              {consents && consents.entries.length > 0 ? (
                <div className="space-y-2">
                  {consents.entries.map((c) => (
                    <div key={c.id} className="flex items-center justify-between text-xs py-2 border-b border-white/5 last:border-0">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{
                          background: c.event === "granted" ? "#4ADE80" : c.event === "withdrawn" ? "#C00000" : "#D4AF37"
                        }} />
                        <span style={{ color: "#F5F5F5" }}>
                          {c.event === "granted" ? (lang === "fr" ? "Consentement accordé" : "Consent granted")
                            : c.event === "withdrawn" ? (lang === "fr" ? "Consentement retiré" : "Consent withdrawn")
                            : (lang === "fr" ? "Mise à jour" : "Updated")}
                        </span>
                        <span style={{ color: "rgba(245,245,245,0.5)" }}>· v{c.terms_version}</span>
                      </div>
                      <span style={{ color: "rgba(245,245,245,0.5)" }}>
                        {new Date(c.timestamp).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { dateStyle: "short", timeStyle: "short" })}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs" style={{ color: "rgba(245,245,245,0.5)" }}>
                  {lang === "fr" ? "Aucun consentement enregistré pour ce compte." : "No consent entries for this account."}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
